import { useEffect, useRef, useState } from "react";
import { fileUrl, getAuthHeaders } from "../api";
import type { FileKind } from "../types";
import { Button } from "./ui/Button";
import ModalBackdrop from "./ui/ModalBackdrop";
import AuthImage from "./AuthImage";
import { formatDate } from "../lib/formatDate";

// Unified preview modal for the Files tab. One component handles every
// file kind so the user gets consistent metadata + actions + prev/next
// navigation regardless of what they clicked.
//
//   - image: rendered inline at the modal's max size
//   - pdf:   full document in a native <iframe> (browser viewer gives
//            zoom + page nav for free) fed the already-fetched blob URL;
//            "Open in new tab" is a secondary fallback for browsers that
//            can't preview a PDF inline
//   - audio: HTML5 <audio controls>
//   - other: file info + Download CTA, no inline preview

export interface PreviewFile {
  key: string;
  recordId: string;
  recordSubject: string;
  recordDate: string;
  r2Key: string;
  filename: string;
  mimeType: string | null;
  sizeBytes: number | null;
  kind: FileKind;
  source: "attachment" | "primary";
  attachmentId: string | null;
}

interface Props {
  caseId: string;
  files: PreviewFile[];
  index: number;
  onIndexChange: (next: number) => void;
  onClose: () => void;
  onDownload: (file: PreviewFile) => void;
  onDelete: (file: PreviewFile) => void;
  onOpenRecord: (recordId: string) => void;
}

async function fetchBlob(url: string): Promise<Blob> {
  const headers = await getAuthHeaders();
  const res = await fetch(url, { credentials: "include", headers });
  if (!res.ok) throw new Error(`http_${res.status}`);
  return res.blob();
}

function formatBytes(n: number | null): string {
  if (n === null || !Number.isFinite(n) || n <= 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function kindLabel(kind: FileKind): string {
  switch (kind) {
    case "pdf":
      return "PDF";
    case "docx":
      return "Word document";
    case "xlsx":
      return "Spreadsheet";
    case "image":
      return "Image";
    case "audio":
      return "Audio";
    case "video":
      return "Video";
    default:
      return "File";
  }
}

export default function FilePreviewModal({
  caseId,
  files,
  index,
  onIndexChange,
  onClose,
  onDownload,
  onDelete,
  onOpenRecord,
}: Props) {
  const file = files[index];

  // Keyboard navigation: Escape closes, arrows move to the next/prev
  // file. Skip arrow handling when an interactive element (audio
  // controls, an input, etc.) is focused so we don't fight the user.
  useEffect(() => {
    function isInteractiveTarget(target: EventTarget | null): boolean {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      return (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "AUDIO" ||
        tag === "VIDEO" ||
        target.isContentEditable
      );
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (isInteractiveTarget(e.target)) return;
      if (e.key === "ArrowRight" && index < files.length - 1) {
        onIndexChange(index + 1);
      } else if (e.key === "ArrowLeft" && index > 0) {
        onIndexChange(index - 1);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [files.length, index, onIndexChange, onClose]);

  if (!file) return null;
  const url = fileUrl(caseId, file.r2Key);

  return (
    <ModalBackdrop onClose={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="file-preview-title"
        className="file-preview-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="file-preview-head">
          <div className="file-preview-titlewrap">
            <h3 id="file-preview-title" className="file-preview-title">
              {file.filename}
            </h3>
            <div className="file-preview-sub">
              {kindLabel(file.kind)} · {formatBytes(file.sizeBytes)} ·{" "}
              {formatDate(file.recordDate, "short")}
              {files.length > 1 ? ` · ${index + 1} of ${files.length}` : ""}
            </div>
          </div>
          <button
            type="button"
            className="file-preview-close"
            onClick={onClose}
            aria-label="Close preview"
          >
            ×
          </button>
        </header>

        <div className="file-preview-body">
          {index > 0 && (
            <button
              type="button"
              className="file-preview-nav file-preview-nav-prev"
              onClick={() => onIndexChange(index - 1)}
              aria-label="Previous file"
            >
              ‹
            </button>
          )}

          <PreviewBody file={file} url={url} />

          {index < files.length - 1 && (
            <button
              type="button"
              className="file-preview-nav file-preview-nav-next"
              onClick={() => onIndexChange(index + 1)}
              aria-label="Next file"
            >
              ›
            </button>
          )}
        </div>

        <footer className="file-preview-foot">
          <button
            type="button"
            className="file-preview-record-link"
            onClick={() => onOpenRecord(file.recordId)}
            title="Open this record"
          >
            ↗ {file.recordSubject}
          </button>
          <span className="file-preview-foot-spacer" />
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onDownload(file)}
          >
            Download
          </Button>
          <Button variant="danger" size="sm" onClick={() => onDelete(file)}>
            Delete
          </Button>
        </footer>
      </div>
    </ModalBackdrop>
  );
}

function PreviewBody({ file, url }: { file: PreviewFile; url: string }) {
  if (file.kind === "image") {
    return (
      <div className="file-preview-image-wrap">
        <AuthImage
          src={url}
          alt={file.filename}
          className="file-preview-image"
          loading="eager"
        />
      </div>
    );
  }
  if (file.kind === "pdf") {
    return <PdfPreview file={file} url={url} />;
  }
  if (file.kind === "audio") {
    return <AudioPreview file={file} url={url} />;
  }
  return (
    <div className="file-preview-other">
      <p>
        Preview not available for {kindLabel(file.kind).toLowerCase()} files.
      </p>
      <p className="file-preview-other-hint">
        Use the Download button below to save it locally.
      </p>
    </div>
  );
}

function AudioPreview({ file, url }: { file: PreviewFile; url: string }) {
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let created: string | null = null;
    (async () => {
      try {
        const blob = await fetchBlob(url);
        if (cancelled) return;
        created = URL.createObjectURL(blob);
        setAudioUrl(created);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    })();
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [url]);

  if (error) {
    return (
      <div className="file-preview-other">
        <p>Couldn't load audio: {error}</p>
      </div>
    );
  }
  if (!audioUrl) {
    return (
      <div className="file-preview-other">
        <p>Loading audio…</p>
      </div>
    );
  }
  return (
    <div className="file-preview-audio">
      <div className="file-preview-audio-icon" aria-hidden>
        🎙
      </div>
      <div className="file-preview-audio-name">{file.filename}</div>
      <audio
        controls
        src={audioUrl}
        className="file-preview-audio-player"
        preload="metadata"
      />
    </div>
  );
}

function PdfPreview({ file, url }: { file: PreviewFile; url: string }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [pageCount, setPageCount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Native PDF viewers vary by browser; if the <iframe> can't render the
  // document we fall back to the open-in-new-tab affordance.
  const [iframeFailed, setIframeFailed] = useState(false);
  const blobUrlRef = useRef<string | null>(null);

  // Fetch the bytes through the auth-aware path once, mint a blob URL for
  // the inline <iframe>, and (best-effort) read the page count. The blob
  // URL is held across re-renders so the new-tab fallback never refetches.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const blob = await fetchBlob(url);
        if (cancelled) return;
        if (!blobUrlRef.current) {
          blobUrlRef.current = URL.createObjectURL(blob);
        }
        setBlobUrl(blobUrlRef.current);
        // Page count: pdfjs is lazy-loaded by pdfThumbnail already, so
        // import("pdfjs-dist") here just hits the same cached chunk.
        // Wrapped in try/catch because page count is a nice-to-have.
        try {
          const bytes = await blob.arrayBuffer();
          if (cancelled) return;
          const pdfjs = await import("pdfjs-dist");
          const doc = await pdfjs.getDocument({ data: bytes }).promise;
          if (!cancelled) setPageCount(doc.numPages);
          await doc.destroy();
        } catch {
          /* page count is optional */
        }
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url]);

  // Revoke on unmount only — keeping the blob URL alive across
  // re-renders lets the open-in-new-tab handler skip a second fetch.
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  async function openInNewTab() {
    try {
      let url2 = blobUrlRef.current;
      if (!url2) {
        const blob = await fetchBlob(url);
        url2 = URL.createObjectURL(blob);
        blobUrlRef.current = url2;
      }
      window.open(url2, "_blank", "noopener,noreferrer");
    } catch (err) {
      window.alert(`Couldn't open: ${(err as Error).message}`);
    }
  }

  return (
    <div className="file-preview-pdf">
      <div className="file-preview-pdf-frame-wrap">
        {error ? (
          <div className="file-preview-other">
            <p>Couldn't load preview: {error}</p>
            <Button variant="secondary" size="sm" onClick={openInNewTab}>
              Open PDF in new tab
            </Button>
          </div>
        ) : iframeFailed ? (
          <div className="file-preview-other">
            <p>This browser can't preview the PDF inline.</p>
            <Button variant="secondary" size="sm" onClick={openInNewTab}>
              Open PDF in new tab
            </Button>
          </div>
        ) : blobUrl ? (
          <iframe
            src={blobUrl}
            title={file.filename}
            className="file-preview-pdf-frame"
            onError={() => setIframeFailed(true)}
          />
        ) : (
          <div className="file-preview-pdf-loading">Loading…</div>
        )}
      </div>
      <div className="file-preview-pdf-meta">
        {file.filename}
        {pageCount !== null && (
          <span className="file-preview-pdf-pages">
            · {pageCount} {pageCount === 1 ? "page" : "pages"}
          </span>
        )}
        {!error && !iframeFailed && blobUrl && (
          <button
            type="button"
            className="file-preview-pdf-newtab"
            onClick={openInNewTab}
          >
            Open in new tab
          </button>
        )}
      </div>
    </div>
  );
}

